import React, { useState, useMemo } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import AdminDataTable from '../../../shared/components/AdminDataTable';
import AdminSubNav from '../../../shared/components/AdminSubNav';
import AdminHeader from '../../../shared/components/AdminHeader';
import { StatusBadge } from '../components/StatusBadge';
import { useAdminCompany } from '../../../shared/contexts/AdminCompanyContext';

interface AdminInvoicesListProps {
  activeSubSection: string;
  onSubSectionChange: (subSection: string) => void;
  brand: BrandConfig;
  Icons: IconsType;
}


const AdminInvoicesList: React.FC<AdminInvoicesListProps> = ({ activeSubSection, onSubSectionChange, brand, Icons }) => {
  const [invoiceFilter, setInvoiceFilter] = useState('readyToPost');
  const { selectedCompanyId } = useAdminCompany();

  const subNavItems = [
    { key: 'invoices', label: 'Invoices', count: 40 },
    { key: 'retainers', label: 'Retainers' },
    { key: 'financeCharges', label: 'Finance Charges' },
    { key: 'legalConnectPay', label: 'LegalConnect Pay' },
  ];

  // Sample invoice data matching the real site
  const sampleInvoicesAll = [
    {
      id: '13816887-01',
      invoiceNumber: '13816887-01',
      billingDate: '01/23/2026',
      orderType: 'Court eFiling',
      account: '110127',
      amount: '$20.39',
      balanceDue: '$20.39',
      paymentOption: 'Credit Card',
      paymentFrequency: 'Weekly',
      invoiceStatus: 'Ready to Post',
      organizationId: 'org-1'
    },
    {
      id: '13816887-02',
      invoiceNumber: '13816887-02',
      billingDate: '01/23/2026',
      orderType: 'eService',
      account: '110127',
      amount: '$4.14',
      balanceDue: '$4.14',
      paymentOption: 'Credit Card',
      paymentFrequency: 'Weekly',
      invoiceStatus: 'Ready to Post',
      organizationId: 'org-1'
    },
    {
      id: '13847047',
      invoiceNumber: '13847047',
      billingDate: '01/23/2026',
      orderType: 'Court eFiling',
      account: '119254',
      amount: '$17.45',
      balanceDue: '$17.45',
      paymentOption: 'Other',
      paymentFrequency: 'Manual',
      invoiceStatus: 'Ready to Post',
      organizationId: 'org-2'
    },
    {
      id: '13846996',
      invoiceNumber: '13846996',
      billingDate: '01/23/2026',
      orderType: 'Court eFiling',
      account: '119254',
      amount: '$19.70',
      balanceDue: '$19.70',
      paymentOption: 'Other',
      paymentFrequency: 'Manual',
      invoiceStatus: 'Ready to Post',
      organizationId: 'org-3'
    },
    {
      id: '13826597',
      invoiceNumber: '13826597',
      billingDate: '01/23/2026',
      orderType: 'Court eFiling',
      account: '193973',
      amount: '$14.96',
      balanceDue: '$14.96',
      paymentOption: 'Credit Card',
      paymentFrequency: 'Manual',
      invoiceStatus: 'Ready to Post',
      organizationId: 'org-4'
    }
  ];

  // Filter invoices by selected company
  const sampleInvoices = useMemo(() => {
    if (!selectedCompanyId) return sampleInvoicesAll;
    return sampleInvoicesAll.filter(invoice => invoice.organizationId === selectedCompanyId);
  }, [selectedCompanyId, sampleInvoicesAll]);

  const columns = [
    {
      key: 'invoiceNumber',
      label: 'Invoice#',
      sortable: true,
      filterable: true
    },
    {
      key: 'billingDate',
      label: 'Billing Date',
      sortable: true,
      filterable: true
    },
    {
      key: 'orderType',
      label: 'Order Type',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'Court eFiling', label: 'Court eFiling' },
        { value: 'eService', label: 'eService' },
        { value: 'Service of Process', label: 'Service of Process' }
      ]
    },
    {
      key: 'account',
      label: 'Account',
      sortable: true,
      filterable: true
    },
    {
      key: 'amount',
      label: 'Amount',
      sortable: true,
      filterable: true
    },
    {
      key: 'balanceDue',
      label: 'Balance Due',
      sortable: true,
      filterable: true
    },
    {
      key: 'paymentOption',
      label: 'Payment Option',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'Credit Card', label: 'Credit Card' },
        { value: 'ACH', label: 'ACH' },
        { value: 'Other', label: 'Other' }
      ]
    },
    {
      key: 'paymentFrequency',
      label: 'Payment Frequency',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'Daily', label: 'Daily' },
        { value: 'Weekly', label: 'Weekly' },
        { value: 'Manual', label: 'Manual' }
      ]
    },
    {
      key: 'invoiceStatus',
      label: 'Invoice Status',
      sortable: true,
      filterable: true,
      render: (value) => (
        <StatusBadge status={value} brand={brand} />
      )
    }
  ];

  const filterOptions = [
    { value: 'readyToPost', label: 'Ready to post' },
    { value: 'posted', label: 'Posted' },
    { value: 'all', label: 'All' }
  ];

  const getSectionTitle = () => {
    switch (activeSubSection) {
      case 'invoices': return 'Invoices';
      case 'retainers': return 'Retainers';
      case 'financeCharges': return 'Finance Charges';
      case 'legalConnectPay': return 'LegalConnect Pay';
      default: return 'Finance';
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ backgroundColor: brand.colors.bgLight }}>
      <AdminHeader title={getSectionTitle()} brand={brand} Icons={Icons}>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <select
              value={invoiceFilter}
              onChange={(e) => setInvoiceFilter(e.target.value)}
              className="px-3 py-2 text-sm border rounded-lg"
              style={{ borderColor: brand.colors.border }}
            >
              {filterOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <button
              className="p-2 border rounded-lg"
              style={{ borderColor: brand.colors.border }}
            >
              <Icons.Search className="w-4 h-4" style={{ color: brand.colors.textLight }} />
            </button>
          </div>

          <button
            className="px-4 py-2 text-sm font-medium rounded-lg"
            style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
          >
            Post Invoices
          </button>
          <button
            className="px-4 py-2 text-sm font-medium rounded-lg border"
            style={{ borderColor: brand.colors.border, color: brand.colors.text }}
          >
            View Batch
          </button>
        </div>
      </AdminHeader>

      <AdminSubNav
        items={subNavItems}
        activeItem={activeSubSection}
        onItemChange={onSubSectionChange}
        brand={brand}
      />

      {/* Status Summary */}
      <div
        className="flex items-center gap-4 px-6 py-3 border-b"
        style={{ backgroundColor: 'white', borderColor: brand.colors.border }}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm" style={{ color: brand.colors.textLight }}>Total Invoices:</span>
          <span className="text-lg font-semibold" style={{ color: brand.colors.text }}>{sampleInvoices.length}</span>
        </div>
        <button
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
          style={{ backgroundColor: '#FEE2E2' }}
        >
          <span className="text-sm font-medium" style={{ color: '#991B1B' }}>48</span>
          <span className="text-xs" style={{ color: '#991B1B' }}>Payment Errors</span>
        </button>
        <button
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
          style={{ backgroundColor: '#FFEDD5' }}
        >
          <span className="text-sm font-medium" style={{ color: '#9A3412' }}>5</span>
          <span className="text-xs" style={{ color: '#9A3412' }}>Post Errors</span>
        </button>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <AdminDataTable
          columns={columns}
          data={sampleInvoices}
          totalCount={sampleInvoices.length}
          showCheckboxes={true}
          showPagination={true}
          onRowClick={(row) => console.log('Invoice clicked:', row)}
          brand={brand}
          Icons={Icons}
        />
      </div>
    </div>
  );
};

export default AdminInvoicesList;
